import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ApiError } from '../../api/client'
import {
  useBedTypes,
  useOrganizationSettings,
  useSaveBedType,
  useSetPpdMode,
} from '../../api/hooks'
import { useAuth } from '../../auth/AuthContext'

/**
 * Patient-day mode and the bed types occupancy is recorded against.
 *
 * @remarks
 * Moved here from the Occupancy screen, which had grown into a mode switch, an editor and
 * a ledger on one page. The first two are configuration set once per installation; the
 * third is data somebody enters as beds fill and empty. Putting the editor behind an
 * operational screen is why it was reported as missing — see `Settings.tsx`.
 *
 * The switch stays WITH the bed types rather than moving to a general organization page,
 * because turning the mode on and having nowhere to record against is a dead end: the
 * first thing anyone needs after the toggle is the list below it.
 */
export function BedTypeSettings() {
  const { hasRole } = useAuth()
  const isAdmin = hasRole('Admin')

  const { data: settings, isLoading } = useOrganizationSettings()
  const ppdEnabled = settings?.ppdModeEnabled ?? false

  const { data: bedTypes = [] } = useBedTypes(ppdEnabled)
  const setMode = useSetPpdMode()
  const saveBedType = useSaveBedType()

  const [editing, setEditing] = useState<number | 'new' | null>(null)
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [weight, setWeight] = useState('1')
  const [error, setError] = useState<string | null>(null)

  if (isLoading) {
    return <p className="px-4 py-3 text-[12px]">Loading…</p>
  }

  function open(bedTypeId: number | 'new') {
    setError(null)
    setEditing(bedTypeId)

    if (bedTypeId === 'new') {
      setCode('')
      setName('')
      setWeight('1')
      return
    }

    const existing = bedTypes.find((b) => b.bedTypeId === bedTypeId)
    setCode(existing?.code ?? '')
    setName(existing?.name ?? '')
    setWeight(String(existing?.relativeWeight ?? 1))
  }

  function save() {
    setError(null)

    const relativeWeight = Number(weight)

    if (Number.isNaN(relativeWeight) || relativeWeight <= 0) {
      setError(
        'The relative weight must be greater than zero. A zero would make this bed type’s ' +
        'cost share vanish while its patient days still counted.',
      )
      return
    }

    saveBedType.mutate(
      {
        bedTypeId: editing === 'new' ? undefined : (editing as number),
        code: code.trim(),
        name: name.trim(),
        relativeWeight,
        sortOrder: 0,
      },
      {
        onSuccess: () => setEditing(null),
        onError: (e) =>
          setError(e instanceof ApiError ? e.message : 'The bed type could not be saved.'),
      },
    )
  }

  return (
    <div className="flex flex-col">
      <section>
        <div
          className="flex flex-wrap items-center gap-3 px-4 py-2"
          style={{ background: 'var(--color-panel-alt)' }}
        >
          <h2 className="col-head">ppd mode</h2>
          <span className="ident">{ppdEnabled ? 'on' : 'off'}</span>

          {isAdmin && (
            <button
              className="text-[12px] underline"
              style={{ color: 'var(--color-accent)' }}
              onClick={() => setMode.mutate(!ppdEnabled)}
              disabled={setMode.isPending}
            >
              {setMode.isPending ? 'Saving…' : ppdEnabled ? 'Turn off' : 'Turn on'}
            </button>
          )}

          <span className="text-[11px]" style={{ color: 'var(--color-faint)' }}>
            Records occupancy by department and bed type, reports cost per patient day, and
            lets a driver allocate cost in proportion to patient days.
          </span>
        </div>

        {setMode.isError && (
          <p
            className="px-4 py-1.5 text-[12px]"
            style={{ background: 'var(--color-error-wash)', color: 'var(--color-error)' }}
            role="alert"
          >
            {setMode.error instanceof ApiError
              ? setMode.error.message
              : 'The setting could not be saved.'}
          </p>
        )}

        {/*
            Turning it off is not destructive and saying so removes the reason to hesitate.
            The ledger survives; only the screens and the driver type go away.
        */}
        {ppdEnabled && isAdmin && (
          <p className="px-4 py-1 text-[11px]" style={{ color: 'var(--color-faint)' }}>
            Turning PPD mode off hides the Occupancy screen and stops patient-days drivers
            being chosen. It deletes nothing — the occupancy ledger is kept.
          </p>
        )}
      </section>

      {!ppdEnabled ? (
        <p className="px-4 py-6 text-[12px]" style={{ color: 'var(--color-faint)' }}>
          PPD mode is off. {isAdmin
            ? 'Turn it on above to record occupancy and report cost per patient day.'
            : 'An administrator can turn it on.'}
        </p>
      ) : (
        <section className="border-t" style={{ borderColor: 'var(--color-rule)' }}>
          <div
            className="flex items-center gap-3 px-4 py-2"
            style={{ background: 'var(--color-panel-alt)' }}
          >
            <h2 className="col-head">bed types</h2>
            <span className="text-[11px]" style={{ color: 'var(--color-faint)' }}>
              The weight is the relative cost of an occupied day. At 1 for every type, each
              bed type reports the same PPD as its department.
            </span>
            {isAdmin && (
              <button className="btn btn-sm ml-auto" onClick={() => open('new')}>
                Add bed type
              </button>
            )}
          </div>

          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b text-left" style={{ borderColor: 'var(--color-rule)' }}>
                <th className="col-head px-4 py-1.5">Code</th>
                <th className="col-head px-4 py-1.5">Name</th>
                <th className="col-head px-4 py-1.5 text-right">Weight</th>
                <th className="col-head px-4 py-1.5 text-right">Census entries</th>
                <th className="col-head px-4 py-1.5" />
              </tr>
            </thead>
            <tbody>
              {bedTypes.map((b) => (
                <tr
                  key={b.bedTypeId}
                  className="border-b"
                  style={{ borderColor: 'var(--color-rule-soft)' }}
                >
                  <td className="ident px-4 py-1.5">
                    {b.code}
                    {!b.isActive && (
                      <span
                        className="badge ml-1.5"
                        style={{
                          background: 'var(--color-panel-alt)',
                          color: 'var(--color-muted)',
                        }}
                      >
                        retired
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-1.5 text-[12px]">{b.name}</td>
                  <td className="ident px-4 py-1.5 text-right">×{b.relativeWeight}</td>
                  <td
                    className="ident px-4 py-1.5 text-right"
                    style={{ color: 'var(--color-muted)' }}
                  >
                    {b.censusEntryCount}
                  </td>
                  <td className="px-4 py-1.5 text-right">
                    {isAdmin && (
                      <button
                        className="text-[12px] underline"
                        style={{ color: 'var(--color-accent)' }}
                        onClick={() => open(b.bedTypeId)}
                      >
                        Edit
                      </button>
                    )}
                  </td>
                </tr>
              ))}

              {bedTypes.length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    className="px-4 py-3 text-[12px]"
                    style={{ color: 'var(--color-faint)' }}
                  >
                    No bed types yet. Occupancy is recorded per bed type, so at least one is
                    needed before anything can be entered.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {editing !== null && (
            <div
              className="flex flex-wrap items-end gap-3 border-t px-4 py-3"
              style={{
                background: 'var(--color-panel-alt)',
                borderColor: 'var(--color-rule-soft)',
              }}
            >
              <label className="flex flex-col gap-1 text-[12px]">
                <span className="col-head">code</span>
                <input
                  className="input w-32"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
              </label>

              <label className="flex flex-col gap-1 text-[12px]">
                <span className="col-head">name</span>
                <input
                  className="input w-64"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </label>

              <label className="flex flex-col gap-1 text-[12px]">
                <span className="col-head">relative weight</span>
                <input
                  className="input w-24"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                />
              </label>

              <button className="btn btn-sm" onClick={save} disabled={saveBedType.isPending}>
                {saveBedType.isPending ? 'Saving…' : 'Save'}
              </button>
              <button
                className="text-[12px] underline"
                style={{ color: 'var(--color-muted)' }}
                onClick={() => setEditing(null)}
              >
                Cancel
              </button>

              <span
                className="text-[11px]"
                style={{ color: 'var(--color-faint)', maxWidth: '26rem' }}
              >
                A weight of 3 says an occupied day here consumes three times what a day at
                weight 1 does. Changing it restates the bed-type split of every period
                recomputed afterwards; department totals are unaffected.
              </span>
            </div>
          )}

          {error && (
            <p
              className="px-4 py-1.5 text-[12px]"
              style={{ background: 'var(--color-error-wash)', color: 'var(--color-error)' }}
              role="alert"
            >
              {error}
            </p>
          )}

          <p className="px-4 py-3 text-[11px]" style={{ color: 'var(--color-faint)' }}>
            Occupancy itself is recorded on the{' '}
            <Link to="/occupancy" className="underline" style={{ color: 'var(--color-accent)' }}>
              Occupancy
            </Link>{' '}
            screen. This page is the setup; that one is the day-to-day.
          </p>
        </section>
      )}
    </div>
  )
}
